<template>
  <PageLayout title="个人分歧终端机">
      <!-- 页面标题区 -->
      <div class="page-header" style="text-align: center">
        <div class="header-icon">
          <Icon name="Psychology" class="header-icon" />
        </div>
        <div class="header-text">
          <h2 class="subtitle">专治选择困难症</h2>
          <p class="motto">"万事不决，量子力学"</p>
        </div>
      </div>
      <!-- 引导区域 -->
      <MysticalCard class="guidance-section" v-if="!isFlipping && !showResult">
        <div class="guidance-content">
          <h3>在开始之前</h3>
          <div class="guidance-steps">
            <div class="step">
              <div class="step-number">1</div>
              <div class="step-content">
                <h3>准备问题</h3>
                <p>在心中默想你想要寻求指引的问题。这个问题应该是明确且具体的。</p>
              </div>
            </div>
            
            <div class="step">
              <div class="step-number">2</div>
              <div class="step-content">
                <h3>冥想时刻</h3>
                <p>花一点时间静心思考你的问题，让内心平静下来。</p>
              </div>
            </div>
            
            <div class="step">
              <div class="step-number">3</div>
              <div class="step-content">
                <h3>抛硬币</h3>
                <p>当你准备好了，点击下方的按钮开始抛硬币。</p>
              </div>
            </div>
          </div>

          <div class="meditation-box">
            <p>让我们一起寻找答案...</p>
          </div>

          <div class="button-container">
            <MysticalButton class="main-button" @click="flipCoin">
              <span class="button-content">
                <span>🎲</span>
                <span class="button-text">抛硬币</span>
              </span>
            </MysticalButton>
          </div>
        </div>
      </MysticalCard>

      <!-- 添加硬币样式选择 -->
      <div class="coin-style-selector" v-if="!isFlipping && !showResult">
        <h4>选择硬币样式</h4>
        <div class="coin-styles">
          <div 
            v-for="style in coinStyles" 
            :key="style.id"
            class="coin-style-option"
            :class="{ 'selected': selectedCoinStyle === style.id }"
            @click="selectCoinStyle(style.id)"
          >
            <div class="preview-coin">
              <img :src="style.frontImage" :alt="`${style.name}正面`" class="coin-front">
            </div>
            <span class="style-name">{{ style.name }}</span>
          </div>
        </div>
      </div>

      <!-- 修改硬币动画区域 -->
      <div class="coin-section" v-if="isFlipping" style="text-align: center">
        <div 
          class="coin" 
          :class="{ 
            'coin-flip': isFlipping,
            [`coin-style-${selectedCoinStyle}`]: true 
          }"
        >
          <div class="coin-side heads">
            <img :src="getCurrentCoinStyle.frontImage" alt="正面">
          </div>
          <div class="coin-side tails">
            <img :src="getCurrentCoinStyle.backImage" alt="反面">
          </div>
        </div>
      </div>

      <!-- 结果反思区域 -->
      <MysticalCard v-if="showResult" class="reflection-section">
        <div class="result-content">
          <h3 style="text-align: center">硬币已落定</h3>
          <div class="coin-result-image" style="text-align: center">
            <img 
              :src="getCurrentCoinStyle[coinResult === 'heads' ? 'frontImage' : 'backImage']" 
              :alt="coinResult === 'heads' ? '正面' : '反面'"
              class="result-coin-img"
            />
          </div>
          <p class="result-text" style="text-align: center">结果是：{{ coinResult === 'heads' ? '正面' : '反面' }}</p>
          
          <div class="insight-box">
            <h4>让我们思考一下：</h4>
            <div class="insight-questions">
              <p>💡 抛硬币是一种适合做简单决策、或验证内心想法的工具，用来排除犹豫。</p>
              <p>如果结果与你的期望一致，你通常不会再抛第二次。</p>
              <p>如果短时间内又抛了一次，说明结果与你的内心希望不一致——这个时候，相信硬币。</p>
            </div>
          </div>

          <div class="wisdom-box">
            <p>你的这些感受，正是内心真实想法的反映。</p>
            <p>如果你想要再抛一次，或许说明你已经有了答案，</p>
            <p>只是还没有勇气承认。</p>
          </div>
          <div class="insight-message">
            <p class="insight-text animate-glow">如果你想要再抛一次，或许说明你已经有了答案</p>
          </div>
          <div class="action-buttons">
            <MysticalButton 
              variant="outline" 
              @click="resetChoice"
              class="action-button"
            >
              <span class="button-content">
                <Icon name="Refresh" class="button-icon" />
                <span class="button-text">重新开始</span>
              </span>
            </MysticalButton>
            <MysticalButton 
              @click="showInsight"
              class="action-button"
            >
              <span class="button-content">
                <Icon name="Bulb" class="button-icon" />
                <span class="button-text">我明白了</span>
              </span>
            </MysticalButton>
            <MysticalButton 
              variant="outline"
              @click="shareVisible = true"
              class="action-button"
              style="border-color:#e60023;color:#e60023"
            >
              <span class="button-content">
                <span class="button-text">📣 分享结果</span>
              </span>
            </MysticalButton>
          </div>
        </div>
      </MysticalCard>
    <ShareResult v-model:visible="shareVisible" scene-label="选择困难症硬币"
      :headline="coinResult === 'heads' ? '硬币说：正面！' : '硬币说：反面！'"
      :subline="'当我想再扔一次的时候，已经知道了内心的想法'"
      page="coin" />
  </PageLayout>
</template>

<style scoped>
.guidance-steps {
  margin: var(--spacing-lg) 0;
}

.step {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-lg);
  margin: var(--spacing-md) 0;
}

.step-number {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mystical);
  color: var(--color-light);
}

.meditation-box {
  background: var(--fh-surface);
  border-radius: var(--radius-lg);
  padding: var(--spacing-lg);
  margin: var(--spacing-lg) 0;
  text-align: center;
  font-family: var(--font-mystical);
  line-height: 1.8;
}

.button-container {
  display: flex;
  justify-content: center;
  margin: var(--spacing-lg) 0;
}

.main-button {
  min-width: 200px;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: var(--spacing-lg);
  margin-top: var(--spacing-xl);
}

.action-button {
  min-width: 150px;
}

.button-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
}

.button-content .material-icons {
  font-size: 1.2rem;
}

.main-button:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-hover);
}

.action-button:hover {
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .action-buttons {
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-md);
  }

  .action-button {
    width: 100%;
    max-width: 300px;
  }
}

/* 添加硬币样式选择器样式 */
.coin-style-selector {
  margin: var(--spacing-xl) 0;
}

.coin-styles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: var(--spacing-md);
  margin-top: var(--spacing-md);
}

.coin-style-option {
  cursor: pointer;
  text-align: center;
  padding: var(--spacing-sm);
  border-radius: var(--radius-md);
  border: 2px solid transparent;
  transition: all var(--transition-normal);
}

.coin-style-option.selected {
  border-color: var(--color-accent);
  background: var(--gradient-mystical);
}

.preview-coin {
  width: 80px;
  height: 80px;
  margin: 0 auto var(--spacing-sm);
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  transition: all var(--transition-normal);
}

.preview-coin img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-coin:hover {
  transform: translateY(-3px) rotate(5deg);
  box-shadow: var(--shadow-hover);
}

.style-name {
  font-size: 0.9rem;
  color: var(--color-light);
}

/* 修改硬币动画样式 */
.coin-section {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  background: var(--fh-overlay);
}

.coin {
  width: 150px;
  height: 150px;
  position: relative;
  transform-style: preserve-3d;
  perspective: 1000px;
}

.coin-flip {
  animation: flipCoin 3s cubic-bezier(0.38, 0.02, 0.09, 1.66) infinite;
}

@keyframes flipCoin {
  0% {
    transform: rotateY(0);
  }
  25% {
    transform: rotateY(900deg);
  }
  50% {
    transform: rotateY(1800deg);
  }
  75% {
    transform: rotateY(2700deg);
  }
  100% {
    transform: rotateY(3600deg);
  }
}

.coin-side {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 0 30px var(--fh-gold-glow);
  transition: all 0.3s ease;
}

.coin-side img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.coin-side.tails {
  transform: rotateY(180deg);
}

/* 为不同硬币样式添加特殊效果 */
.coin-style-ancient .coin-side {
  filter: sepia(0.5);
  box-shadow: 0 0 30px rgba(139, 69, 19, 0.3);
}

.coin-style-rmb .coin-side {
  filter: brightness(1.2);
  box-shadow: 0 0 30px var(--fh-gold-glow);
}

/* Container (.coin-view) removed — PageLayout slot provides layout */

/* 页面标题区 */
.page-header {
  margin-bottom: 8px;
}

.header-text {
  margin-top: var(--spacing-sm);
}

.header-text .subtitle {
  font-size: 1.2rem;
  margin-bottom: var(--spacing-xs);
  color: var(--color-light);
}

.header-text .motto {
  font-size: 1rem;
  color: var(--color-text-light);
  opacity: 0.8;
}

/* 引导区域 */
.guidance-section {
  margin-top: var(--spacing-md);
}

.guidance-steps {
  margin: var(--spacing-lg) 0;
}

.step {
  margin: var(--spacing-md) 0;
}

.meditation-box {
  margin: var(--spacing-lg) 0;
  padding: var(--spacing-lg);
}

.button-container {
  margin: var(--spacing-lg) 0;
}

/* 响应式调整 */
@media (max-width: 768px) {
  
  .page-header {
    margin-bottom: 4px;
  }

  .coin {
    width: 120px;
    height: 120px;
    max-width: 200px;
  }

  .guidance-section {
    margin-top: var(--spacing-sm);
  }
}

/* 醒目提示语样式 */
.insight-message {
  text-align: center;
  margin: var(--spacing-xl) 0;
  padding: var(--spacing-lg);
  background: rgba(var(--color-primary-rgb), 0.1);
  border-radius: var(--radius-lg);
}

.insight-text {
  font-family: var(--font-mystical);
  font-size: 1.3rem;
  color: var(--color-accent);
  line-height: 1.6;
  margin: 0;
}

.result-coin-img {
  width: 100%;
  max-width: 120px;
  height: auto;
  display: block;
  margin: 0 auto;
}

.animate-glow {
  animation: insightGlow 2s ease-in-out infinite alternate;
}

/* 400px 超小屏幕断点 */
@media (max-width: 400px) {
  .result-coin-img {
    max-width: 80px;
  }

  .preview-coin {
    width: 60px;
    height: 60px;
  }

  .main-button {
    min-width: 150px;
  }

  .page-header {
    margin-bottom: 2px;
  }

  .coin-section,
  .reflection-section {
    padding: 0;
  }

  .action-button {
    width: 100%;
  }
}

@keyframes insightGlow {
  from {
    text-shadow: 0 0 10px var(--color-accent),
                 0 0 20px var(--color-accent),
                 0 0 30px var(--color-primary);
  }
  to {
    text-shadow: 0 0 20px var(--color-accent),
                 0 0 30px var(--color-accent),
                 0 0 40px var(--color-primary);
  }
}
</style>

<script setup>
import { ref, computed } from 'vue'
import { MysticalButton, MysticalCard } from '@/components/common'
import Icon from '@/components/common/Icon.vue'
import PageLayout from '@/components/layout/PageLayout.vue'
import { ShareResult } from '@/components/common'
import { API_CONFIG } from '@/config'

// 记录交互的辅助函数
const logInteraction = async (type, question, result, interpretation) => {
  try {
    const token = localStorage.getItem('token')
    if (!token) return // 未登录不记录

    await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.persona.interaction}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        type,
        question,
        result,
        interpretation
      })
    })
  } catch (error) {
    // 静默失败，不影响用户体验
    console.error('记录交互失败:', error)
  }
}

const isFlipping = ref(false)
const showResult = ref(false)
const coinResult = ref('')
const shareVisible = ref(false)
const result = ref(null)

// 硬币样式配置
const coinStyles = [
  {
    id: 'rmb',
    name: '人民币',
    preview: '/coins/rmb/preview.png',
    frontImage: '/coins/rmb/front.png',
    backImage: '/coins/rmb/back.png'
  },
  {
    id: 'ancient',
    name: '铜钱',
    preview: '/coins/ancient/preview.png',
    frontImage: '/coins/ancient/front.png',
    backImage: '/coins/ancient/back.png'
  },
  {
    id: 'dollar',
    name: '美元',
    preview: '/coins/dollar/preview.png',
    frontImage: '/coins/dollar/front.png',
    backImage: '/coins/dollar/back.png'
  },
  {
    id: 'euro',
    name: '欧元',
    preview: '/coins/euro/preview.png',
    frontImage: '/coins/euro/front.png',
    backImage: '/coins/euro/back.png'
  }
]

const selectedCoinStyle = ref('rmb') // 默认使用人民币样式

const getCurrentCoinStyle = computed(() => {
  return coinStyles.find(style => style.id === selectedCoinStyle.value)
})

const selectCoinStyle = (styleId) => {
  selectedCoinStyle.value = styleId
}

const flipCoin = async () => {
  isFlipping.value = true
  showResult.value = false

  // 硬币翻转动画时间
  await new Promise(resolve => setTimeout(resolve, 3000))

  coinResult.value = Math.random() > 0.5 ? 'heads' : 'tails'
  isFlipping.value = false
  showResult.value = true

  // 记录交互
  await logInteraction('coin', '命运硬币决策', {
    result: coinResult.value,
    coinStyle: selectedCoinStyle.value
  }, null)
}

const resetChoice = () => {
  isFlipping.value = false
  showResult.value = false
  coinResult.value = ''
}

const showInsight = () => {
  // 可以添加一些额外的顿悟提示
  resetChoice()
}
</script> 